import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Lock, Unlock, Pin, PinOff, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Discussions = () => {
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDiscussions();
  }, []);

  const fetchDiscussions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/discussions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setDiscussions(data);
      }
    } catch (error) {
      console.error('Failed to fetch discussions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLock = async (discussionId, isLocked) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/discussions/${discussionId}/lock?is_locked=${!isLocked}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        fetchDiscussions();
      }
    } catch (error) {
      console.error('Failed to update lock status:', error);
    }
  };

  const handlePin = async (discussionId, isPinned) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/discussions/${discussionId}/pin?is_pinned=${!isPinned}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        fetchDiscussions();
      }
    } catch (error) {
      console.error('Failed to update pin status:', error);
    }
  };

  const handleDelete = async (discussionId) => {
    if (!window.confirm('Are you sure you want to delete this discussion?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/discussions/${discussionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchDiscussions();
      }
    } catch (error) {
      console.error('Failed to delete discussion:', error);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Discussions</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage community discussions</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Title</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Author</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Category</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Replies</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Likes</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Status</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">Loading...</td>
                  </tr>
                ) : discussions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">No discussions found</td>
                  </tr>
                ) : (
                  discussions.map((discussion) => (
                    <tr key={discussion.id} className="border-t dark:border-gray-700">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {discussion.is_pinned && <Pin size={14} className="text-red-500" />}
                          {discussion.is_locked && <Lock size={14} className="text-gray-500" />}
                          <span className="font-medium text-gray-900 dark:text-white">{discussion.title}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-gray-900 dark:text-white">{discussion.user?.name}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{discussion.category}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{discussion.replies?.length || 0}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{discussion.likes || 0}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          {discussion.is_pinned && (
                            <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">Pinned</span>
                          )}
                          {discussion.is_locked && (
                            <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">Locked</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleLock(discussion.id, discussion.is_locked)}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                            title={discussion.is_locked ? 'Unlock' : 'Lock'}
                          >
                            {discussion.is_locked ? <Unlock size={16} /> : <Lock size={16} />}
                          </button>
                          <button
                            onClick={() => handlePin(discussion.id, discussion.is_pinned)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title={discussion.is_pinned ? 'Unpin' : 'Pin'}
                          >
                            {discussion.is_pinned ? <PinOff size={16} /> : <Pin size={16} />}
                          </button>
                          <button
                            onClick={() => handleDelete(discussion.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Discussions;